import { XIcon } from "lucide-react";
import { type MouseEvent, useCallback, useMemo } from "react";
import { useTranslations } from "../../i18n";
import { IconButton } from "../Button/IconButton";
import type { ISelectOption } from "../Select/types";
import { Spinner } from "../Spinner";
import { type ISpinnerSize } from "../Spinner/types";
import type {
  IComboBoxColor,
  IComboBoxSize,
  IComboBoxTriggerProps,
} from "./types";
import { useComboBoxTrigger } from "./useComboBoxTrigger";
import { textFieldButtonColor, textFieldButtonSize } from "../../utils";
import { FieldAccessory } from "../FieldAccessory";
import { type ITagColor, Tag } from "@/ui-kit";
import { clsx } from "clsx";

export const ComboBoxMultiTrigger = (props: IComboBoxTriggerProps = {}) => {
  const { t } = useTranslations();

  const {
    disabled,
    size,
    color = "layer1",
    placeholder,
    inputId,
    searchValue,
    setSearchValue,
    onClear,
    showClearButton,
    invalid,
    loading,
    onSelect,
    multiple,
    value,
    open,
    contentId,
    activeDescId,
    onInputKeyDownCapture,
    onInputFocus,
    dataAttributes,
    searchInputRef,
  } = useComboBoxTrigger();

  const selectedOptions = useMemo((): ISelectOption[] => {
    if (!multiple || !value) {
      return [];
    }

    const valueArray = Array.isArray(value) ? value : [value];
    return valueArray;
  }, [multiple, value]);

  const {
    ref: referenceRef,
    onClick,
    onFocus,
    onKeyDown,
    onMouseDown,
    onPointerDown,
    onPointerEnter,
    id: referenceId,
    className,

    role: _role,
    tabIndex: _tabIndex,
    "aria-expanded": _ariaExpanded,
    "aria-haspopup": _ariaHaspopup,
    "aria-controls": _ariaControls,
    "aria-activedescendant": _ariaActiveDescendant,

    ...restReferenceProps
  } = props;

  const handleBadgeRemove = useCallback(
    (optionId: string) => {
      const optionToRemove = selectedOptions.find((opt) => opt.id === optionId);
      if (optionToRemove) {
        onSelect(optionToRemove);
        searchInputRef.current?.focus();
      }
    },
    [selectedOptions, onSelect, searchInputRef],
  );

  const handleClear = useCallback(() => {
    onClear?.();
    searchInputRef.current?.focus();
  }, [onClear, searchInputRef]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.(e as MouseEvent<HTMLInputElement>);
      searchInputRef.current?.focus();
    },
    [onClick, searchInputRef],
  );

  const spinnerSizeMap: Record<IComboBoxSize, ISpinnerSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const tagColor: Record<IComboBoxColor, ITagColor> = {
    layer1: "layer2",
    layer2: "layer3",
  };

  const clearButtonJSX = !disabled && !loading && showClearButton && (
    <IconButton
      variant="filled"
      size={textFieldButtonSize[size]}
      color={textFieldButtonColor[color]}
      onClick={handleClear}
      content={<XIcon />}
      aria-label={t("ui.comboBox.clearSelection")}
    />
  );

  const spinnerJSX = !disabled && loading && (
    <FieldAccessory size={size}>
      <Spinner color="primary" size={spinnerSizeMap[size]} />
    </FieldAccessory>
  );

  const rootClass = clsx(
    "nuco-combobox__trigger nuco-combobox__trigger-multi",
    `nuco-combobox__trigger--${size}`,
    `nuco-combobox__trigger--${color}`,
    invalid && "nuco-combobox__trigger--invalid",
  );

  return (
    <div
      ref={referenceRef}
      id={referenceId}
      className={rootClass}
      onClick={handleClick}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      {...restReferenceProps}
      role={undefined}
      tabIndex={-1}
      aria-haspopup={undefined}
      aria-expanded={undefined}
      aria-controls={undefined}
      aria-activedescendant={undefined}
      style={{ outlineOffset: "-1px" }}
    >
      <div className="nuco-combobox__trigger-inner">
        {selectedOptions.map((option) => (
          <Tag
            key={option.id}
            id={option.id}
            text={option.text}
            startIcon={option.icon}
            color={tagColor[color]}
            size={size}
            onRemove={
              !disabled ? () => handleBadgeRemove(option.id) : undefined
            }
            disabled={disabled}
          />
        ))}
        <input
          ref={searchInputRef}
          id={inputId}
          {...dataAttributes}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={contentId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescId}
          className="nuco-combobox__trigger-input nuco-typography--body"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={onInputFocus}
          onKeyDownCapture={onInputKeyDownCapture}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>
      <div className="nuco-combobox__trigger-actions">
        {spinnerJSX || clearButtonJSX}
      </div>
    </div>
  );
};
